import React, { useState, useRef } from 'react';
import { X, Trash, Sparkle, FilePdf, Image as ImageIcon, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const FREQS = ['daily', 'twice_daily', 'thrice_daily', 'weekly', 'as_needed'];

export const PrescriptionScanner = ({ api, onClose, onSaved }) => {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scan, setScan] = useState(null);
  const [memberName, setMemberName] = useState('');
  const [meds, setMeds] = useState([]);
  const fileRef = useRef();

  const pickFile = (f) => {
    if (!f) return;
    const ok = /\.(jpg|jpeg|png|webp|pdf)$/i.test(f.name);
    if (!ok) { toast.error('Use a JPG, PNG, WEBP photo or PDF'); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error('Max 10 MB'); return; }
    setFile(f);
  };

  const doScan = async () => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/health/prescriptions/scan', fd, { timeout: 90000 });
      setScan(r.data);
      setMemberName(r.data.matched_member || '');
      setMeds((r.data.extracted.medicines || []).map(m => ({ ...m, times_str: (m.times || []).join(', ') })));
      setStep('review');
      if (r.data.matched_member) toast.success(`Detected patient: ${r.data.matched_member}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed — try a clearer photo');
    }
    setScanning(false);
  };

  const updateMed = (i, key, val) => setMeds(prev => prev.map((m, j) => j === i ? { ...m, [key]: val } : m));

  const doConfirm = async () => {
    if (!memberName) { toast.error('Select who this prescription is for'); return; }
    if (meds.length === 0) { toast.error('No medicines to add'); return; }
    setSaving(true);
    try {
      const payload = {
        member_name: memberName,
        doctor: scan.extracted.doctor || '',
        date: scan.extracted.date || '',
        diagnosis: scan.extracted.diagnosis || '',
        file_url: scan.file_url,
        save_record: true,
        medicines: meds.map(m => ({ ...m, times: m.times_str.split(',').map(s => s.trim()).filter(Boolean) })),
      };
      const r = await api.post('/health/prescriptions/confirm', payload);
      toast.success(`${r.data.medicines_created} medicine(s) added for ${memberName}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="prescription-scanner">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Sparkle size={20} className="text-purple-500" /> Scan Prescription</h2>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        {step === 'upload' && (
          <div className="space-y-4">
            <div onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-border rounded-2xl p-10 text-center cursor-pointer hover:border-[#FF6B35]/60 transition-colors"
              data-testid="prescription-dropzone">
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
                onChange={e => pickFile(e.target.files?.[0])} data-testid="prescription-file-input" />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  {file.type === 'application/pdf' ? <FilePdf size={40} className="text-red-500" /> : <ImageIcon size={40} className="text-blue-500" />}
                  <p className="text-sm font-semibold">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)} MB — tap to change</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <UploadSimple size={40} className="text-muted-foreground" />
                  <p className="text-sm font-semibold">Tap to upload or drag a prescription here</p>
                  <p className="text-xs text-muted-foreground">Photo (JPG/PNG/WEBP) or PDF · max 10 MB</p>
                </div>
              )}
            </div>
            <button onClick={doScan} disabled={!file || scanning} data-testid="scan-prescription-btn"
              className="w-full py-3 rounded-full bg-purple-600 text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              <Sparkle size={16} /> {scanning ? 'Reading prescription… (10–30s)' : 'Scan with AI'}
            </button>
          </div>
        )}

        {step === 'review' && scan && (
          <div className="space-y-4" data-testid="prescription-review">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">For member *</label>
                <select value={memberName} onChange={e => setMemberName(e.target.value)} data-testid="prescription-member-select"
                  className={`w-full mt-1 px-3 py-2 rounded-lg border bg-background text-sm ${!memberName ? 'border-amber-500' : 'border-border'}`}>
                  <option value="">— Who is this for? —</option>
                  {scan.members.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {!scan.matched_member && <p className="text-[11px] text-amber-600 mt-1">Patient name not detected — please select</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Doctor</label>
                <input value={scan.extracted.doctor || ''} onChange={e => setScan({ ...scan, extracted: { ...scan.extracted, doctor: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Dr. name" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Date</label>
                <input type="date" value={scan.extracted.date || ''} onChange={e => setScan({ ...scan, extracted: { ...scan.extracted, date: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
              </div>
            </div>
            {scan.extracted.diagnosis && <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2">Diagnosis: {scan.extracted.diagnosis}</p>}

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">Extracted medicines — review & edit before saving</p>
              <div className="space-y-3">
                {meds.map((m, i) => (
                  <div key={i} className="border border-border rounded-xl p-3 space-y-2" data-testid={`extracted-med-${i}`}>
                    <div className="flex items-center gap-2">
                      <input value={m.name} onChange={e => updateMed(i, 'name', e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-semibold" placeholder="Medicine name" />
                      <button onClick={() => setMeds(prev => prev.filter((_, j) => j !== i))}
                        className="p-2 text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <input value={m.dosage || ''} onChange={e => updateMed(i, 'dosage', e.target.value)}
                        placeholder="Dosage (5ml)" className="px-2 py-1.5 rounded-lg border border-border bg-background text-xs" />
                      <select value={m.frequency} onChange={e => updateMed(i, 'frequency', e.target.value)}
                        className="px-2 py-1.5 rounded-lg border border-border bg-background text-xs">
                        {FREQS.map(f => <option key={f} value={f}>{f.replace('_', ' ')}</option>)}
                      </select>
                      <input value={m.times_str} onChange={e => updateMed(i, 'times_str', e.target.value)}
                        placeholder="08:00, 20:00" className="px-2 py-1.5 rounded-lg border border-border bg-background text-xs" />
                      <input type="date" value={m.end_date || ''} onChange={e => updateMed(i, 'end_date', e.target.value)}
                        title="End date (auto-expires after this)" className="px-2 py-1.5 rounded-lg border border-border bg-background text-xs" />
                    </div>
                    {m.notes && <p className="text-[11px] text-muted-foreground">📝 {m.notes}</p>}
                    {m.end_date && <p className="text-[11px] text-green-600">Auto-expires after {m.end_date}{m.duration_days ? ` (${m.duration_days} days)` : ''}</p>}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setStep('upload')} className="px-4 py-2.5 rounded-full border border-border text-sm font-semibold">← Back</button>
              <button onClick={doConfirm} disabled={saving || !memberName} data-testid="confirm-prescription-btn"
                className="flex-1 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold disabled:opacity-50">
                {saving ? 'Saving…' : `Add ${meds.length} Medicine(s) for ${memberName || '…'}`}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground text-center">The prescription file will be saved as a Health Record. Kid medicines automatically appear in Kids Zone dose tracker.</p>
          </div>
        )}
      </div>
    </div>
  );
};
